import alertTokens from "@amzn/meridian-tokens/base/icon/alert";
import checkCircleTokens from "@amzn/meridian-tokens/base/icon/check-circle";
import clipboardTokens from "@amzn/meridian-tokens/base/icon/clipboard";
import documentTokens from "@amzn/meridian-tokens/base/icon/document";
import geopinTokens from "@amzn/meridian-tokens/base/icon/geopin";
import globeTokens from "@amzn/meridian-tokens/base/icon/globe";
import menuMeatballTokens from "@amzn/meridian-tokens/base/icon/menu-meatball";
import systemTokens from "@amzn/meridian-tokens/base/icon/system";
import {
  CHANGE_TYPE_LABELS,
  isInfrastructureFailure,
  reasonCodeLabel,
  regionShortLabel,
} from "../adminReportUtils";
import { ChangeType, RegionCode } from "../types";
import Chip, { ChipTone } from "./chip";

const DOCUMENT_TYPE_CHIPS: Record<string, { tokens: any; tone: ChipTone }> = {
  PROCEDURE: { tokens: clipboardTokens, tone: "teal" },
  GUIDELINE: { tokens: documentTokens, tone: "blue" },
  POLICY: { tokens: documentTokens, tone: "purple" },
};

/** null renders a quiet dash: nothing happened, so nothing to draw attention. */
const CHANGE_TONES: Record<ChangeType, ChipTone | null> = {
  EXISTING: null,
  VERSION_UPDATE: "amber",
  METADATA_UPDATE: "neutral",
  NOW_INCLUDED: "green",
  NOW_EXCLUDED: "red",
  RESTORED: "teal",
  REMOVED: "red",
  NEW: "blue",
};

/** High emphasis chip naming the region currently being browsed. */
export const RegionChip = ({ region }: { region: RegionCode }) => (
  <Chip tone="blue" solid>
    {regionShortLabel(region)}
  </Chip>
);

/** Whether a document is region-local or promoted into every region. */
export const ScopeChip = ({
  region,
  isGlobal,
}: {
  region: RegionCode;
  isGlobal: boolean;
}) =>
  isGlobal ? (
    <Chip tone="neutral" tokens={globeTokens}>
      Global
    </Chip>
  ) : (
    <Chip tone="blue" tokens={geopinTokens}>
      {regionShortLabel(region)}
    </Chip>
  );

/** node_document_type, e.g. PROCEDURE. */
export const DocumentTypeChip = ({
  documentType,
}: {
  documentType: string;
}) => {
  const chip = DOCUMENT_TYPE_CHIPS[documentType] ?? {
    tokens: menuMeatballTokens,
    tone: "neutral" as ChipTone,
  };
  const label = documentType.charAt(0) + documentType.slice(1).toLowerCase();

  return (
    <Chip tone={chip.tone} tokens={chip.tokens}>
      {label}
    </Chip>
  );
};

/** node_status, which the policy tree sets. */
export const NodeStatusChip = ({ status }: { status: string }) => (
  <Chip tone={status === "active" ? "green" : "neutral"} dot>
    {status.charAt(0).toUpperCase() + status.slice(1)}
  </Chip>
);

/** What the latest sync did. Unchanged documents render as a quiet dash. */
export const ChangeChip = ({ changeType }: { changeType: ChangeType }) => {
  const tone = CHANGE_TONES[changeType];

  return tone ? (
    <Chip tone={tone}>{CHANGE_TYPE_LABELS[changeType]}</Chip>
  ) : (
    <span className="admin-report__doc-sub">—</span>
  );
};

/** Whether an indexed document has a test case validating retrieval. */
export const CoverageChip = ({ testCaseId }: { testCaseId?: string }) =>
  testCaseId ? (
    <Chip tone="green" tokens={checkCircleTokens}>
      Covered
    </Chip>
  ) : (
    <Chip tone="red" tokens={alertTokens}>
      Uncovered
    </Chip>
  );

/** Why a test case failed, toned by whether rewording can fix it. */
export const ReasonCodeChip = ({ reasonCode }: { reasonCode: string }) => {
  const infrastructure = isInfrastructureFailure(reasonCode);

  return (
    <Chip
      tone={infrastructure ? "purple" : "red"}
      tokens={infrastructure ? systemTokens : alertTokens}
    >
      {reasonCodeLabel(reasonCode)}
    </Chip>
  );
};
